/**
 * **Die Auszeichnung eines Positionstextes** — Auftrag `PT-lib` (2026-09-04),
 * Task 1 des Plans
 * `docs/superpowers/plans/2026-09-04-positionstext-auszeichnung-umsetzung.md`,
 * Spec `docs/superpowers/specs/2026-09-04-positionstext-auszeichnung-design.md`
 * Abschnitte 4.2, 4.3, 5 und 6.3.
 *
 * Drei Ausfuhren über **einem** Zerleger:
 *
 * - `lesePositionstext(text)` — der **tolerante** Leser. Nimmt jede
 *   Zeichenkette, wirft **nie** (`E-805`) und liefert die flache Zeilenliste
 *   aus Spec 4.2.
 * - `pruefePositionstext(text)` — die **strenge** Erfassungsprüfung. Liefert
 *   eine Befundliste; leer heisst zugelassen.
 * - `alsKlartext(zeilen)` — die Projektion derselben Struktur zurück in Text
 *   ohne Auszeichnung (Spec 6.3).
 *
 * **Warum eine Struktur und nie eine HTML-Zeichenkette** (`D-5`). Eine
 * Zeichenkette bräuchte am Ende jedes Konsumenten einen unmaskierten
 * Einsetzpunkt — `dangerouslySetInnerHTML` in React oder ein fünftes Konstrukt
 * in `apps/api/src/render/template-engine.ts`, das über sich selbst sagt „Es
 * gibt keinen Weg zu unmaskierter Ausgabe". Mit einer Struktur kommt das
 * Markup aus dem **Konsumenten**; aus den Daten kommen ausschliesslich
 * Zeichenketten und zwei Wahrheitswerte, und die Sicherheitsaussage ist eine
 * Eigenschaft der Bauform statt einer Verabredung über den Gebrauch einer
 * Hintertür.
 *
 * **Warum EIN Zerleger für beide Funktionen** (Spec 5.1). Zwei Parser über
 * dieselbe Sprache fallen erst auseinander, wenn ein Text die Prüfung besteht
 * und der Leser an ihm scheitert — dieselbe Festlegung, aus der
 * `apps/api/src/mailvorlagen/vorlagenpruefer.ts` die Ausdrücke aus
 * `template-engine.ts` importiert statt sie nachzubauen. Der Unterschied
 * zwischen beiden liegt allein in der **Strenge**, nicht in der Zerlegung.
 *
 * **Warum diese Datei in `packages/contracts` liegt und kein eigenes Paket
 * ist** (`D-6`): Beide Enden brauchen sie — `apps/web` für Anzeige und
 * Vorschau, `apps/api` für Druck und Projektion —, und `@fameacc/contracts`
 * ist die einzige Abhängigkeit, die beide bereits führen.
 *
 * **Keine Abhängigkeit, auch nicht `zod`.** Was hier steht, sind reine
 * Funktionen über Text; sie laufen im Browser und im Dienst wortgleich
 * (`D-13`). Der Zod-Baustein, der `pruefePositionstext` an die zwei
 * Positionsverträge hängt, entsteht in `PT-vertrag` und gehört dorthin.
 *
 * **Es wird nichts dekodiert** (`D-8`): `&amp;` bleibt die fünf Zeichen. Ein
 * Dekodierer wäre eine zweite, unsichtbare Umformung an einem
 * GoBD-geschützten Text — ohne Anlass, mit einer eigenen Fehlerklasse
 * (doppelte Dekodierung) und ohne Weg zurück.
 */
/**
 * Der Fehlercode der Erfassungsprüfung (`D-15`). Genau einer, weil der
 * Erfassende immer dasselbe wissen muss: **welcher** Tag beanstandet wurde.
 * Das steht im Feld `tag`, nicht in einem zweiten Code.
 */
export const POSITIONSTEXT_MARKUP_UNZULAESSIG = 'POSITIONSTEXT_MARKUP_UNZULAESSIG';
/**
 * Die Whitelist beim **Erfassen** (`D-1`): genau fünf Namen, ohne Attribut.
 *
 * Vier davon hat der Auftraggeber genannt; `i` steht dabei, weil er im
 * Altbestand 364-mal vorkommt und ohne ihn 364 Positionstexte nicht anzeigbar
 * wären.
 */
export const POSITIONSTEXT_TAGS_ERFASSEN = ['br', 'b', 'i', 'ul', 'li'];
/**
 * Die Whitelist beim **Lesen** (`D-1`): dieselben fünf plus `p`.
 *
 * `p` kommt im Altbestand sechsmal vor — zu selten, um ein Erfassungsmittel zu
 * sein, zu häufig, um ihn beim Anzeigen zu verlieren.
 */
export const POSITIONSTEXT_TAGS_LESEN = [...POSITIONSTEXT_TAGS_ERFASSEN, 'p'];
/**
 * Ein Konstrukt, das wie ein Tag aussieht — und nur eines.
 *
 * **Kein Leerraum zwischen `<` und dem Namen, und das ist Absicht.** `a < b > c`
 * ist gewöhnlicher Text und darf nicht als Tag `b` gelesen werden (`E-813`);
 * `<br >` ist einer und wird gelesen (Spec 4.3). Der Unterschied liegt genau an
 * dieser Stelle, weil ein Mensch, der einen Tag schreibt, ihn nicht mit einem
 * Leerzeichen beginnt.
 *
 * `[^<>]*` statt `.*` hält das Konstrukt innerhalb eines `<…>`-Paares: Ein
 * unabgeschlossenes `<b` bleibt Text, und ein `<` innerhalb des Restes beginnt
 * einen neuen Versuch statt den alten zu verlängern.
 */
const TAGFORM = /^<(\/?)([A-Za-z][A-Za-z0-9]*)([^<>]*)>/;
/**
 * Zerlegt den Text in Text- und Tagstücke. Wirft nie, kennt keine Whitelist und
 * trifft keine Entscheidung über Zulässigkeit — das tun die beiden Auswerter.
 */
function zerlege(text) {
    const stuecke = [];
    let puffer = '';
    let pufferStelle = 0;
    let i = 0;
    const pufferAusgeben = () => {
        if (puffer.length > 0) {
            stuecke.push({ art: 'text', text: puffer, stelle: pufferStelle });
            puffer = '';
        }
    };
    while (i < text.length) {
        if (text[i] !== '<') {
            if (puffer.length === 0)
                pufferStelle = i;
            puffer += text[i];
            i += 1;
            continue;
        }
        const treffer = TAGFORM.exec(text.slice(i));
        if (treffer === null) {
            // Ein `<`, dem kein Tag folgt: `5 < 7`, `a<b`, `<3`. Text (`E-813`).
            if (puffer.length === 0)
                pufferStelle = i;
            puffer += '<';
            i += 1;
            continue;
        }
        const roh = treffer[0];
        const schraegstrich = treffer[1] ?? '';
        const name = treffer[2] ?? '';
        const rest = treffer[3] ?? '';
        pufferAusgeben();
        const restGetrimmt = rest.trim();
        const selbstschliessend = restGetrimmt === '/';
        stuecke.push({
            art: 'tag',
            name: name.toLowerCase(),
            schliessend: schraegstrich === '/',
            selbstschliessend,
            attribute: restGetrimmt === '' || selbstschliessend ? null : restGetrimmt,
            stelle: i,
            roh,
        });
        i += roh.length;
    }
    pufferAusgeben();
    return stuecke;
}
/**
 * Zieht benachbarte Läufe mit gleicher Auszeichnung zusammen und wirft leere
 * weg (Spec 4.2: „ein Lauf mit leerem Text fällt weg").
 *
 * Ohne das zerfiele `a<b></b>b` in drei Läufe, von denen einer leer ist — und
 * jeder Konsument müsste dieselbe Straffung selbst noch einmal machen.
 */
function straffeLaeufe(laeufe) {
    const gestrafft = [];
    for (const lauf of laeufe) {
        if (lauf.text.length === 0)
            continue;
        const letzter = gestrafft[gestrafft.length - 1];
        if (letzter !== undefined && letzter.fett === lauf.fett && letzter.kursiv === lauf.kursiv) {
            gestrafft[gestrafft.length - 1] = { ...letzter, text: letzter.text + lauf.text };
            continue;
        }
        gestrafft.push(lauf);
    }
    return gestrafft;
}
/**
 * **Der tolerante Leser.** Wirft nie (`E-805`) und liefert für jede Eingabe
 * eine Zeilenliste.
 *
 * Er kennt `br b i ul li p` (`POSITIONSTEXT_TAGS_LESEN`). Alles andere —
 * unbekannter Tag, `</br>`, die selbstschliessende Form eines Behälters —
 * erscheint als **Text**, wörtlich so, wie es im Positionstext steht. Ein
 * Attribut an einem zugelassenen Tag macht ihn dagegen **nicht** zu Text
 * (Nachtrag 2026-09-04, `PT-attributlesen`): `<i class="…">` wirkt wie
 * `<i>`, das Attribut wird stillschweigend verworfen und erreicht die
 * Ausgabe nie (`D-5`). Unbalancierte Auszeichnung wird am Ende geschlossen,
 * `<ul>` in `<ul>` flach gemacht, ein `<li>` ausserhalb eines `<ul>` zu einem
 * Listenpunkt (Spec 5.1).
 *
 * **Warum er nie wirft.** Er liest den Altbestand, und der ist nicht
 * verhandelbar (`D-4`). Ein Leser, der bei kaputter Auszeichnung wirft, macht
 * aus einem hässlichen Positionstext einen Beleg, der sich nicht mehr drucken
 * lässt.
 */
export function lesePositionstext(text) {
    const zeilen = [];
    let laeufe = [];
    let art = 'zeile';
    let fett = false;
    let kursiv = false;
    const textAnhaengen = (stueck) => {
        if (stueck.length > 0)
            laeufe.push({ text: stueck, fett, kursiv });
    };
    /**
     * Beendet die laufende Zeile.
     *
     * `erzwingen` trennt die zwei Fälle aus Spec 4.2: Ein `<br>` ist ein
     * **gewollter** Abstand und gibt deshalb auch eine leere Zeile aus
     * (`A<br><br>B` sind drei Zeilen, die mittlere leer); ein `<ul>` oder ein
     * `<li>` beendet nur, was offen ist, und erzeugt keine Leerzeile — sonst
     * begänne `<ul><li>x</li></ul>` mit einer.
     */
    const zeileBeenden = (erzwingen, naechsteArt) => {
        const gestrafft = straffeLaeufe(laeufe);
        if (gestrafft.length > 0 || erzwingen)
            zeilen.push({ art, laeufe: gestrafft });
        laeufe = [];
        art = naechsteArt;
    };
    for (const stueck of zerlege(text)) {
        if (stueck.art === 'text') {
            textAnhaengen(stueck.text);
            continue;
        }
        if (!POSITIONSTEXT_TAGS_LESEN.includes(stueck.name)) {
            textAnhaengen(stueck.roh);
            continue;
        }
        // Nachtrag 2026-09-04 (`PT-attributlesen`): Ein Attribut an einem
        // zugelassenen Tag macht den Leser nicht blind — `<i class="…">` wirkt
        // wie `<i>`. Anlass: 15 importierte Positionszeilen mit zusammen 105
        // Icon-Tags der Form `<i class="…" style="…">` aus dem alten
        // Webinterface; ohne diese Ausnahme erschien die Zeile wörtlich als Text
        // statt als kursiver Lauf. `stueck.attribute` wird ab hier nirgends mehr
        // gelesen — die Attribute erreichen die Ausgabe nie (`D-5`).
        switch (stueck.name) {
            case 'br':
                // `</br>` ist kein Tag dieser Liste (Spec 4.3) und erscheint als Text.
                if (stueck.schliessend)
                    textAnhaengen(stueck.roh);
                else
                    zeileBeenden(true, 'zeile');
                break;
            case 'ul':
                if (stueck.selbstschliessend)
                    textAnhaengen(stueck.roh);
                else
                    zeileBeenden(false, 'zeile');
                break;
            case 'li':
                if (stueck.selbstschliessend)
                    textAnhaengen(stueck.roh);
                else
                    zeileBeenden(false, stueck.schliessend ? 'zeile' : 'listenpunkt');
                break;
            case 'p':
                if (stueck.selbstschliessend)
                    textAnhaengen(stueck.roh);
                else
                    zeileBeenden(false, 'zeile');
                break;
            case 'b':
                if (stueck.selbstschliessend)
                    textAnhaengen(stueck.roh);
                else
                    fett = !stueck.schliessend;
                break;
            case 'i':
                if (stueck.selbstschliessend)
                    textAnhaengen(stueck.roh);
                else
                    kursiv = !stueck.schliessend;
                break;
            default:
                textAnhaengen(stueck.roh);
                break;
        }
    }
    zeileBeenden(false, 'zeile');
    // „Eine leere Zeile am Ende (etwa aus einem abschliessenden `</ul>`) faellt
    // weg" (Spec 4.2). Zwischen zwei Zeilen bleibt sie stehen — sie ist dort der
    // gewollte Abstand.
    while (zeilen.length > 0 && (zeilen[zeilen.length - 1]?.laeufe.length ?? 0) === 0) {
        zeilen.pop();
    }
    return zeilen;
}
/** Ein Tag in der Form, in der eine Meldung ihn nennt: `<b>`, `</b>`. */
function nenne(name, schliessend = false) {
    return schliessend ? `</${name}>` : `<${name}>`;
}
const ZUGELASSEN_TEXT = POSITIONSTEXT_TAGS_ERFASSEN.map((t) => nenne(t)).join(', ');
function befund(tag, stelle, meldung) {
    return { code: POSITIONSTEXT_MARKUP_UNZULAESSIG, tag, stelle, meldung };
}
/**
 * **Die strenge Erfassungsprüfung.** Liefert eine Befundliste; eine leere Liste
 * heisst „zugelassen".
 *
 * Sie nimmt genau `br b i ul li` an, ohne Attribut (`E-806`, `E-807`), weist
 * `<p>` ab (`E-808`) und verlangt balancierte Auszeichnung: kein offenes `<b>`,
 * kein `</ul>` ohne `<ul>`, kein `<ul>` in `<ul>`, kein `<li>` ausserhalb eines
 * `<ul>` (`E-809`, `D-7`).
 *
 * **Sie ist die Decke, nicht der Boden** (`D-10`). Der Boden ist die
 * Datenbankbedingung aus Migration `0154`, und der ist absichtlich weiter: Er
 * lässt `p` durch und prüft keine Attribute, weil er auch für den Altimport
 * gilt — einen Bestand, den `D-4` zu ändern verbietet.
 *
 * **Ein `<` ohne Auszeichnungsabsicht ist kein Befund** (`E-813`): `5 < 7` und
 * `a<b` gehen durch, weil sie Text sind und kein Markup.
 */
export function pruefePositionstext(text) {
    const befunde = [];
    /** Die offenen Behälter- und Auszeichnungstags, in der Reihenfolge ihres Öffnens. */
    const offen = [];
    for (const stueck of zerlege(text)) {
        if (stueck.art === 'text')
            continue;
        if (!POSITIONSTEXT_TAGS_ERFASSEN.includes(stueck.name)) {
            befunde.push(befund(stueck.name, stueck.stelle, `Der Tag ${nenne(stueck.name, stueck.schliessend)} ist im Positionstext nicht zugelassen. ` +
                `Zugelassen sind: ${ZUGELASSEN_TEXT}.`));
            continue;
        }
        if (stueck.attribute !== null) {
            befunde.push(befund(stueck.name, stueck.stelle, `Der Tag ${nenne(stueck.name)} darf kein Attribut tragen. ` +
                `Zugelassen sind: ${ZUGELASSEN_TEXT}, jeweils ohne Attribut.`));
            // Weiter im Text: der Tag ist der Struktur nach in Ordnung, nur sein
            // Attribut nicht. Ein zweiter Befund über eine dadurch scheinbar
            // unbalancierte Auszeichnung waere ein Folgefehler und kein Fund.
        }
        if (stueck.name === 'br') {
            if (stueck.schliessend) {
                befunde.push(befund('br', stueck.stelle, `Der Tag </br> ist nicht zugelassen. ${nenne('br')} hat keine schliessende Form; ` +
                    `zugelassen sind <br>, <br/> und <br />.`));
            }
            continue;
        }
        if (stueck.selbstschliessend) {
            befunde.push(befund(stueck.name, stueck.stelle, `Die selbstschliessende Form <${stueck.name}/> ist nicht zugelassen; ` +
                `sie gilt nur fuer <br>.`));
            continue;
        }
        if (!stueck.schliessend) {
            if (stueck.name === 'ul' && offen.some((o) => o.name === 'ul')) {
                befunde.push(befund('ul', stueck.stelle, `Der Tag ${nenne('ul')} steht in einem ${nenne('ul')}; verschachtelte Listen sind nicht zugelassen.`));
            }
            if (stueck.name === 'li' && !offen.some((o) => o.name === 'ul')) {
                befunde.push(befund('li', stueck.stelle, `Der Tag ${nenne('li')} steht ausserhalb eines ${nenne('ul')}.`));
            }
            offen.push({ name: stueck.name, stelle: stueck.stelle });
            continue;
        }
        const oberster = offen[offen.length - 1];
        if (oberster === undefined || oberster.name !== stueck.name) {
            befunde.push(befund(stueck.name, stueck.stelle, `Der schliessende Tag ${nenne(stueck.name, true)} hat kein passendes ${nenne(stueck.name)}.`));
            continue;
        }
        offen.pop();
    }
    for (const nichtGeschlossen of offen) {
        befunde.push(befund(nichtGeschlossen.name, nichtGeschlossen.stelle, `Der Tag ${nenne(nichtGeschlossen.name)} wird geoeffnet und nicht geschlossen.`));
    }
    return befunde;
}
/**
 * **Die Klartextprojektion** (Spec 6.3): dieselbe Struktur zurück in Text ohne
 * Auszeichnung.
 *
 * | Element | Klartext |
 * |---|---|
 * | Zeilenwechsel | U+000A |
 * | Listenpunkt | `- ` vor dem Text |
 * | fett, kursiv | der Text allein, keine Markierung |
 * | Zeile ohne Lauf | eine Leerzeile |
 *
 * **Der Zeilenwechsel trennt, er terminiert nicht:** Nach der letzten Zeile
 * steht kein U+000A, auch nicht nach einem letzten Listenpunkt. Der heutige
 * Konsument ist der Rumpf der Versandmail (`document_versand.koerper_text`,
 * Migration `0151`), und ein angehängter Leerzeilenrest ist dort Rauschen.
 *
 * Im Ergebnis steht danach kein Tag mehr (`E-810`) — ein `<` und ein `>`
 * ausdrücklich schon, wenn sie im Original als Text standen (`5 < 7`, `E-813`).
 *
 * **Konsumenten heute und später:** heute der Mailrumpf; später die
 * Positionsbeschreibung einer ausgehenden XRechnung/ZUGFeRD (`D-16`, nicht in
 * dieser Ära) und der DATEV-Export.
 */
export function alsKlartext(zeilen) {
    return zeilen
        .map((zeile) => {
        const inhalt = zeile.laeufe.map((lauf) => lauf.text).join('');
        return zeile.art === 'listenpunkt' ? `- ${inhalt}` : inhalt;
    })
        .join('\n');
}
/**
 * **Die Normalisierung eines Alt-Positionstextes beim Import** — Auftrag
 * `AI-positionstextimport` (2026-09-05), Plan
 * `docs/superpowers/plans/2026-09-03-cutover-neuaufbau-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-04-positionstext-auszeichnung-design.md`
 * Nachtrag Abschnitt 11 (Altimport-Grenze).
 *
 * **Der Anlass.** Der Probelauf gegen den echten Dump (Phase 7, Klasse
 * `gebucht`) scheiterte an `document_line_description_markup` (Migration
 * `0154`): Der Altbestand trägt Whitelist-Tags MIT Attributen — 15 Zeilen mit
 * `<i class="…" style="…">`-Icons, gemessen von `pnpm positionstext:bestand`
 * (`E-816`) — und die Bedingung prüft keine Attribute, sie lehnt sie ab, weil
 * ein Attribut den geforderten unmittelbaren Abschluss `>` verhindert
 * (Kopfkommentar `0154`, Abschnitt „DIE BEDINGUNG PRÜFT KEINE ATTRIBUTE").
 * `lesePositionstext()` (der TOLERANTE Leser) verzeiht ein Attribut an einem
 * zugelassenen Tag längst (`PT-attributlesen`) — die DATENBANK verzeiht es
 * nicht. Diese Funktion schliesst die Lücke am Eingang: Sie normalisiert VOR
 * dem Schreiben, statt die Datenbank scheitern zu lassen.
 *
 * **Warum hier und nicht in `packages/altimport-parser`.** Die Regeln — welche
 * fünf Tags bleiben, wie `br` in seinen drei Schreibweisen zusammenfällt, wie
 * unbalancierte Auszeichnung geschlossen wird — sind exakt die Regeln, die
 * {@link zerlege} schon für {@link lesePositionstext} und
 * {@link pruefePositionstext} kennt. `altimport-parser` hat keine Abhängigkeit
 * auf `@fameacc/contracts` (und umgekehrt keine hierher) — eine zweite
 * Tag-Grammatik dort wäre eine zweite Wahrheit über dieselbe Sprache. Der
 * Aufrufer (`apps/api/src/cutover/rechnungszubringer.ts`) importiert bereits
 * aus `@fameacc/contracts`; die Abhängigkeitsrichtung zeigt hierher.
 *
 * **Die Regel in Kürze:**
 * - Ein Attribut an `br`, `b`, `i`, `ul`, `li` fällt weg — `<i class="…">`
 *   wird `<i>`.
 * - `br` fällt in jeder Schreibweise (`<br>`, `<br/>`, `<br />`, `<BR>`,
 *   selbst das im Bestand nicht vorkommende `</br>`) auf die eine kanonische
 *   Form `<br>` zusammen — es trägt weder Inhalt noch Zustand.
 * - Jeder andere Tag — `<span>`, `<div>`, `<script>`, und **`p`**, das die
 *   Erfassungsprüfung ({@link pruefePositionstext}) ausdrücklich abweist —
 *   verschwindet; sein Inhalt bleibt stehen, weil er ein eigenes Textstück
 *   daneben ist und nie Teil des Tags war.
 * - Eine selbstschliessende Form von `b`/`i`/`ul`/`li` (`<b/>`) trägt nie
 *   Inhalt und fällt weg wie ein leeres Element.
 * - Unbalancierte Auszeichnung wird wiederhergestellt: ein `<ul>` in einem
 *   bereits offenen `<ul>` bleibt ungeöffnet (`lesePositionstext` macht genau
 *   das flach), ein `<li>` ausserhalb eines offenen `<ul>` bleibt Text ohne
 *   Tag, ein schliessender Tag ohne passendes Gegenstück verschwindet, und
 *   was am Textende offen bleibt, wird geschlossen.
 *
 * **Was sie ausdrücklich nicht löst** (dieselbe benannte Grenze wie im
 * Kopfkommentar von Migration `0154`): ein blankes `<` ohne Tag-Absicht
 * (`5 < 7`) besteht {@link pruefePositionstext} (`E-813`), aber nicht die
 * SQL-Bedingung — dafür bräuchte die Bedingung selbst die Unterscheidung
 * zwischen einem tag-ähnlichen und einem beiläufigen `<`, die sie laut ihrem
 * eigenen Kopfkommentar bewusst nicht trägt. Im gemessenen Altbestand kommt
 * das nicht vor (Spec 4.3: für „alles andere" null Treffer).
 *
 * **Ergebnisgarantie.** Das Ergebnis besteht {@link pruefePositionstext} mit
 * einer leeren Befundliste — vorausgesetzt, der Ausgangstext enthält kein
 * blankes `<` ohne Tag-Absicht (siehe oben). Es enthält nach der Bereinigung
 * keinen anderen Tag als `br`, `b`, `i`, `ul`, `li`, nie ein Attribut, und ist
 * bezüglich dieser fünf Tags immer balanciert.
 *
 * **Ein leeres Ergebnis ist kein Ergebnis.** Bestünde der Text ausschliesslich
 * aus entferntem Markup (kein sichtbares Zeichen bliebe übrig), liefert diese
 * Funktion stattdessen das ORIGINAL ohne jedes Tag — auch ohne die fünf
 * Whitelist-Tags — statt eine leere Zeichenkette: Ein Positionstext ohne jeden
 * sichtbaren Inhalt ist ein Mangel des Altbestands (derselbe Fall wie ein
 * leeres Pflichtfeld, `D-19`), keiner, den die Bereinigung noch verschärfen
 * sollte.
 *
 * **Entities werden nicht dekodiert** (`D-8`, wie überall in dieser Datei):
 * `&amp;` bleibt die fünf Zeichen.
 */
export function bereinigePositionstext(text) {
    const bereinigt = normalisiereMarkup(text);
    // "Leer" heisst hier SICHTBAR leer -- eine ueberlebende Tag-Huelle ohne
    // Inhalt (`<b></b>`, aus `<div><b></b></div>`) zaehlt als leer wie eine
    // echte leere Zeichenkette. Bereinigung verwirft nie ein Textstueck (jeder
    // `text`-Baustein aus `zerlege()` landet unveraendert in der Ausgabe), also
    // ist die sichtbare Textmenge in `bereinigt` IMMER identisch mit der im
    // ORIGINAL -- der Rueckfall unten liefert deshalb in genau diesem Fall
    // ohnehin dieselbe leere Zeichenkette und raeumt nur die leere Huelle weg.
    if (nurText(bereinigt).trim().length > 0)
        return bereinigt;
    return nurText(text);
}
/** Nur die Textstuecke, alles Markup weg -- ohne Ruecksicht auf die Whitelist. */
function nurText(text) {
    return zerlege(text)
        .filter((stueck) => stueck.art === 'text')
        .map((stueck) => (stueck.art === 'text' ? stueck.text : ''))
        .join('');
}
/**
 * Baut den Text mit ausschliesslich den fuenf Whitelist-Tags neu auf, ohne
 * Attribute und mit balancierter Auszeichnung — der Kern von
 * {@link bereinigePositionstext}.
 *
 * **Warum ein Stapel und keine zwei Durchläufe.** Ein schliessender Tag muss
 * wissen, ob sein öffnendes Gegenstück wirklich ausgegeben wurde (ein
 * verschachteltes `<ul>` wird unterdrückt und sein `</ul>` darf das äussere
 * `<ul>` nicht fälschlich mitschliessen) — deshalb trägt jeder Stapeleintrag
 * neben dem Namen, ob er `emittiert` wurde. Ein schliessender Tag ohne
 * passendes offenes Gegenstück verschwindet stillschweigend; ein schliessender
 * Tag, der ein anderes, noch offenes Element überspringt (`<b><i>x</b></i>`),
 * schliesst das übersprungene Element zuerst — dieselbe Wiederherstellung
 * sauberer Schachtelung, die `lesePositionstext()` für den Leseweg beschreibt
 * („Unbalancierte Auszeichnung wird am Ende geschlossen").
 */
function normalisiereMarkup(text) {
    const ausgabe = [];
    const offen = [];
    const schliesseBis = (zielIndex) => {
        while (offen.length > zielIndex) {
            const oben = offen.pop();
            if (oben !== undefined && oben.emittiert)
                ausgabe.push(`</${oben.name}>`);
        }
    };
    for (const stueck of zerlege(text)) {
        if (stueck.art === 'text') {
            ausgabe.push(stueck.text);
            continue;
        }
        // `br` faellt in jeder Form -- offen, geschlossen, selbstschliessend --
        // auf die eine kanonische Form zusammen; es traegt keinen Zustand und
        // steht deshalb nicht auf dem Stapel.
        if (stueck.name === 'br') {
            ausgabe.push('<br>');
            continue;
        }
        if (!POSITIONSTEXT_TAGS_ERFASSEN.includes(stueck.name)) {
            // Fremder Tag -- auch `p`, das die Erfassungspruefung abweist: weg. Sein
            // Inhalt steht bereits als eigenes Textstueck daneben.
            continue;
        }
        if (stueck.selbstschliessend) {
            // `<b/>`, `<ul/>`, `<li/>` tragen nie Inhalt -- weg wie ein leeres Element.
            continue;
        }
        if (!stueck.schliessend) {
            const emittiert = stueck.name === 'ul'
                ? !offen.some((o) => o.name === 'ul')
                : stueck.name === 'li'
                    ? offen.some((o) => o.name === 'ul')
                    : true;
            offen.push({ name: stueck.name, emittiert });
            if (emittiert)
                ausgabe.push(`<${stueck.name}>`);
            continue;
        }
        // Schliessender Tag: das naechstliegende offene Gegenstueck suchen, alles
        // darueber vorher schliessen, ein Tag ohne passendes Gegenstueck
        // verschwindet stillschweigend.
        const vonOben = [...offen].reverse().findIndex((o) => o.name === stueck.name);
        if (vonOben === -1)
            continue;
        schliesseBis(offen.length - 1 - vonOben);
    }
    schliesseBis(0);
    return ausgabe.join('');
}
//# sourceMappingURL=positionstext.js.map